import type { Data } from '@strapi/types';
import { useTranslations } from '@power-core/toolkit';
import { type FunctionComponent, useState } from 'react';

import { useDropdownDateOptions } from '@/modules/utils/custom-hooks/use-dropdown-date-options';
import { Button } from '@/ui-components/button/button';
import { Dropdown } from '@/ui-components/dropdown/dropdown';
import { SearchableDropdown } from '@/ui-components/dropdown/searchable-dropdown';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Input } from '@/ui-components/input/input';
import { Paragraph } from '@/ui-components/paragraph/paragraph';

import { useSharedDocumentsApi } from '../hooks/use-shared-documents-api';

export const NewDocumentAccess: FunctionComponent<{
	setIsNew: React.Dispatch<React.SetStateAction<boolean>>;
	documentDocumentId: Pick<Data.Entity<'api::project-document.project-document'>, 'documentId'>['documentId'];
}> = ({ setIsNew, documentDocumentId }) => {
	const t = useTranslations();
	const { createAccess } = useSharedDocumentsApi(documentDocumentId);
	const [guestEmail, setGuestEmail] = useState('');
	const [period, setPeriod] = useState<string[]>([]);
	const [accessLevel, setAccessLevel] = useState<'edit' | 'view'>('view');
	const { dateOptions } = useDropdownDateOptions();
	const shareDateOptions = [
		...dateOptions.slice(0, -1),
		{ value: 'unlimited', label: t('T_UNLIMITED_INVITATION') },
		...dateOptions.slice(-1),
	];

	const handleCreateNewDocumentAccess = () => {
		createAccess
			.mutateAsync({
				documentDocumentId,
				body: {
					guestEmail: guestEmail.trim(),
					accessLevel,
					startsAt: period[0] || null,
					expiresAt: period[1] || null,
					unlimited: period.length === 0,
				},
			})
			.then(() => {
				setIsNew(false);
				setGuestEmail('');
				setPeriod([]);
				setAccessLevel('view');
			});
	};

	return (
		<Flexbox direction='col' className='gap-1 w-full'>
			<hr className='border-t-1 my-0.5 border-n300_table_background' />
			<Input
				name='userEmail'
				value={guestEmail}
				placeholder={t('T_ENTER_USER_EMAIL')}
				onChange={(e) => setGuestEmail(e.target.value)}
				className='w-full'
				sizing='small'
			/>
			<Flexbox className='gap-4 items-baseline' justify='between'>
				<SearchableDropdown
					sizing='small'
					onSelect={(value) => Array.isArray(value) && setPeriod([value[0], value[1]])}
					hasDatePicker
					calendarDirection='future'
					inputBackground='light'
					options={shareDateOptions}
					placeholder={t('T_SELECT_PERIOD')}
					onClear={() => setPeriod([])}
					className='w-64'
					fixedStartDate={new Date()}
				/>
				<Flexbox className='gap-1' items='center'>
					<Dropdown
						inline
						isMenu
						textSize='sm'
						arrowIcon={true}
						label={
							accessLevel === 'edit' ? (
								<Paragraph variant='text-sm'>{t('T_CAN_EDIT')}</Paragraph>
							) : (
								<Paragraph variant='text-sm'>{t('T_CAN_VIEW')}</Paragraph>
							)
						}
						menuItems={[
							{
								value: 'edit',
								label: <span className='text-sm w-full text-left px-4 py-3'>{t('T_CAN_EDIT')}</span>,
								onClick: () => setAccessLevel('edit'),
							},
							{
								value: 'view',
								label: <span className='text-sm w-full text-left px-4 py-3'>{t('T_CAN_VIEW')}</span>,
								onClick: () => setAccessLevel('view'),
							},
						]}
					/>
					<Button
						variant='text'
						sizing='small'
						onClick={() => handleCreateNewDocumentAccess()}
						className='font-normal text-sm w-fit text-accent'
					>
						{t('T_SAVE')}
					</Button>
					<Button variant='text' sizing='small' onClick={() => setIsNew(false)} className='font-normal text-sm w-fit text-brand'>
						{t('T_CANCEL')}
					</Button>
				</Flexbox>
			</Flexbox>
			<Paragraph variant='text-xs' className='!text-brand'>
				{t('T_DOCUMENT_ACCESS_INFO')}
			</Paragraph>
		</Flexbox>
	);
};
